import { useEffect, useId, useState } from "react";
import { CalendarDays } from "lucide-react";
import { Button as AriaButton, Dialog, DialogTrigger, Popover } from "react-aria-components";
import { formatDateSr, parseDateSrToIso } from "../../lib/format";
import { CalendarGrid } from "./CalendarGrid";
import { useModalPortalContainer } from "./Modal";
import { useLocale } from "../../i18n/LocaleProvider";
import "./DateField.css";

export interface DateFieldProps {
  label?: string;
  /** Internal value — ISO "YYYY-MM-DD", or "" for empty. Never a full datetime. */
  value: string;
  /** Fires only once the typed text is a valid dd.mm.gggg date (or cleared to ""). */
  onChange: (isoValue: string) => void;
  required?: boolean;
  /** External error (e.g. post-submit server/Zod validation) — takes precedence over the field's own format error. */
  error?: string | null;
  hint?: string;
  /** Shown when the typed text doesn't parse as a real dd.mm.gggg date, once the field is blurred. Caller-supplied so it's translatable (sr/hr/en). */
  invalidFormatMessage: string;
  id?: string;
  disabled?: boolean;
  /** aria-label for the calendar-icon trigger button. Caller-supplied so it's translatable (sr/hr/en). */
  calendarButtonLabel: string;
  previousMonthLabel: string;
  nextMonthLabel: string;
}

/**
 * A date field guaranteeing user-facing dd.mm.gggg display/entry regardless
 * of browser/OS locale — unlike `<input type="date">`, whose displayed
 * format follows the browser's locale (can render mm/dd/yyyy), which the
 * product's date-display contract forbids
 * (NORMAX_PERFORMANCE_SLICE_B_PRODUCTION_USABILITY_FIX_REPORT.md §10-12).
 * Wraps `formatDateSr`/`parseDateSrToIso`
 * (`@normax-performance/shared-format`) — explicit component validation
 * (day/month/year, leap years), never `new Date("dd.mm.gggg")` string
 * parsing. Internal value stays ISO "YYYY-MM-DD" — the API/DB contract is
 * completely unchanged.
 */
export function DateField({
  label,
  value,
  onChange,
  required,
  error,
  hint,
  invalidFormatMessage,
  id,
  disabled,
  calendarButtonLabel,
  previousMonthLabel,
  nextMonthLabel,
}: DateFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [text, setText] = useState(() => (value ? formatDateSr(value) : ""));
  const [formatError, setFormatError] = useState<string | null>(null);
  const { intlLocale } = useLocale();
  const portalContainer = useModalPortalContainer();

  // Keep the displayed text in sync when the ISO value changes externally
  // (e.g. programmatic reset/prefill) — this effect only fires when the
  // controlled `value` prop itself changes, never on each keystroke.
  useEffect(() => {
    setText(value ? formatDateSr(value) : "");
    setFormatError(null);
  }, [value]);

  function handleTextChange(next: string) {
    setText(next);
    if (next.trim() === "") {
      setFormatError(null);
      onChange("");
      return;
    }
    const iso = parseDateSrToIso(next.trim());
    if (iso) {
      setFormatError(null);
      onChange(iso);
    }
    // Non-empty and not yet a valid date: just update the displayed text —
    // no error shown mid-typing, only checked on blur below.
  }

  function handleBlur() {
    if (text.trim() === "") return;
    const iso = parseDateSrToIso(text.trim());
    setFormatError(iso ? null : invalidFormatMessage);
  }

  /** Calendar-day pick drives the exact same text/onChange path as valid typed input — one date contract, not two. */
  function handleCalendarSelect(iso: string, closePopover: () => void) {
    setText(formatDateSr(iso));
    setFormatError(null);
    onChange(iso);
    closePopover();
  }

  const shownError = error ?? formatError;
  const errorId = shownError ? `${inputId}-error` : undefined;
  const hintId = hint ? `${inputId}-hint` : undefined;

  return (
    <div className="nx-field">
      {label ? (
        <label className="nx-field__label" htmlFor={inputId}>
          {label}
          {required ? <span className="nx-field__required">*</span> : null}
        </label>
      ) : null}
      <div className="nx-datefield__control">
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          placeholder="dd.mm.gggg"
          maxLength={10}
          className={["nx-input", shownError ? "nx-input--error" : ""].filter(Boolean).join(" ")}
          value={text}
          disabled={disabled}
          aria-invalid={shownError ? true : undefined}
          aria-describedby={[errorId, hintId].filter(Boolean).join(" ") || undefined}
          onChange={(e) => handleTextChange(e.target.value)}
          onBlur={handleBlur}
        />
        <DialogTrigger>
          <AriaButton
            className="nx-datefield__calendar-trigger"
            aria-label={calendarButtonLabel}
            isDisabled={disabled}
          >
            <CalendarDays size={16} />
          </AriaButton>
          <Popover
            className="nx-datefield__popover"
            UNSTABLE_portalContainer={portalContainer}
            placement="bottom start"
          >
            <Dialog className="nx-datefield__dialog">
              {({ close }) => (
                <CalendarGrid
                  selectedIso={value}
                  onSelect={(iso) => handleCalendarSelect(iso, close)}
                  intlLocale={intlLocale}
                  labels={{ previousMonth: previousMonthLabel, nextMonth: nextMonthLabel }}
                />
              )}
            </Dialog>
          </Popover>
        </DialogTrigger>
      </div>
      {hint && !shownError ? (
        <span id={hintId} className="nx-field__hint">
          {hint}
        </span>
      ) : null}
      {shownError ? (
        <span id={errorId} className="nx-field__error" role="alert">
          {shownError}
        </span>
      ) : null}
    </div>
  );
}
